import React from 'react';
import type { AssistantOnboardingId } from '@chatsdet/types';
import type { KickoffRequest } from './onboardingFlow';

type AssistantContextValue = {
  openAssistant: (prompt?: string) => void;
  /** Hand the assistant an onboarding to run — see `onboardingFlow.ts`. */
  startOnboarding: (id: AssistantOnboardingId, params?: Record<string, string>) => void;
  /** Open the ordinary assistant panel and send a hidden first turn into a fresh thread — see `onboardingFlow.ts`'s `KickoffRequest`. */
  startThreadWithKickoff: (request: KickoffRequest) => void;
  /**
   * Open the panel on a FRESH thread and send `text` as the first visible
   * user turn. `params` is merged into `pageContext.params` for the
   * lifetime of this ask's thread (mirrors `KickoffRequest.params`) — every
   * caller has the relevant entity (e.g. `propertyId`) to hand, so this is
   * required, not optional.
   */
  askAssistant: (text: string, params: Record<string, string>) => void;
};

const AssistantContext = React.createContext<AssistantContextValue | null>(null);

export function AssistantProvider({
  openAssistant,
  startOnboarding,
  startThreadWithKickoff,
  askAssistant,
  children,
}: AssistantContextValue & { children: React.ReactNode }) {
  const value = React.useMemo(
    () => ({ openAssistant, startOnboarding, startThreadWithKickoff, askAssistant }),
    [openAssistant, startOnboarding, startThreadWithKickoff, askAssistant]
  );

  return <AssistantContext.Provider value={value}>{children}</AssistantContext.Provider>;
}

export function useAssistant() {
  const context = React.useContext(AssistantContext);
  if (!context) {
    throw new Error('useAssistant must be used within an AssistantProvider');
  }
  return context;
}
